import type { Listing } from "@lumora/bridge";
import { listingImageUrl } from "./images.js";

/** Shape expected by original Al-Amir Car / views (price in dollars, images as URLs). */
export type LegacyCarJson = {
  id: string;
  year: number;
  make: string;
  model: string;
  price: number;
  purchase_cost: number;
  mileage: string;
  archive: boolean;
  featured: boolean;
  images: string[];
  description: string;
  color: string;
  investors: [];
};

export function listingToLegacyCar(listing: Listing, featured = false): LegacyCarJson {
  return {
    id: listing.id,
    year: listing.year,
    make: listing.make,
    model: listing.model,
    price: listing.priceCents / 100,
    purchase_cost: (listing.purchaseCents ?? 0) / 100,
    mileage: String(listing.mileage ?? ""),
    archive: listing.status !== "available",
    featured,
    images: listing.imageKeys.map((k) => listingImageUrl(k)).filter((u): u is string => !!u),
    description: listing.description ?? "",
    color: "",
    investors: [],
  };
}

export function listingsToLegacyCars(listings: Listing[]): LegacyCarJson[] {
  const available = listings.filter((l) => l.status === "available");
  const featuredIds = new Set(available.slice(0, 3).map((l) => l.id));
  return listings.map((l) => listingToLegacyCar(l, featuredIds.has(l.id)));
}

/** Map legacy Car JSON (dollars, image URLs) to Lumora listing write body. */
export function legacyCarToListingInput(car: LegacyCarJson & { vin?: string }): {
  id?: string;
  make: string;
  model: string;
  year: number;
  priceCents: number;
  purchaseCents: number;
  status: "available" | "sold" | "archived";
  mileage: number;
  vin: string;
  description: string;
  imageKeys: string[];
} {
  const archive = car.archive === true || String(car.archive) === "true";
  const status = archive ? "archived" : "available";
  const imageKeys = (car.images ?? []).map((url) => {
    const u = url.trim();
    if (!u) return "";
    if (!u.startsWith("http")) return u.replace(/^\/+/, "");
    try {
      const parsed = new URL(u);
      const path = parsed.pathname.replace(/^\/+/, "");
      if (path.startsWith("tenants/")) return path;
      const dataIdx = path.indexOf("data/");
      if (dataIdx >= 0) return path.slice(dataIdx + 5);
      return path;
    } catch {
      return u;
    }
  }).filter(Boolean);

  return {
    id: car.id || undefined,
    make: car.make || "TBD",
    model: car.model || "Vehicle",
    year: Number(car.year) || new Date().getFullYear(),
    priceCents: Math.round(Number(car.price || 0) * 100),
    purchaseCents: Math.round(Number(car.purchase_cost || 0) * 100),
    status,
    mileage: parseInt(String(car.mileage || "0"), 10) || 0,
    vin: car.vin ?? "",
    description: car.description ?? "",
    imageKeys,
  };
}
